import { useLayoutEffect } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useAuthorization } from 'components/Authorization';
import usePromoteDays, { PROMOTE_I18N_NS } from 'hooks/business/usePromoteDays';
import { i18nExist } from 'services/i18n/utils';
import { useAnnualActivity } from '../annualActivity';
import { usePriceZones, usePricingCatalog } from './usePricing';

interface MarketPromotionSelection {
  orgId: number;
  priceZone?: string;
  annualPackage?: Pay.DurationDescV2;
}

export const marketPromotionSelectionState = atom<MarketPromotionSelection | undefined>(undefined);

/** 仅选购界面发布套餐选择；概况与账单页沿用默认方案的活动。 */
export function useMarketPromotionSelection(
  orgId: number,
  priceZone: string | undefined,
  annualPackage: Pay.DurationDescV2 | undefined,
  enabled = true,
) {
  const setSelection = useSetAtom(marketPromotionSelectionState);
  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    const selection = { orgId, priceZone, annualPackage };
    setSelection(selection);
    return () => {
      setSelection((current) => (current === selection ? undefined : current));
    };
  }, [orgId, priceZone, annualPackage, enabled, setSelection]);
}

/** 入口角标和 Banner 共用此结果，页面选中态优先于默认方案。 */
export function useMarketPromotion() {
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const orgId = Number(payload?.orgId);
  const validOrganization = Number.isFinite(orgId) && orgId > 0;
  const selection = useAtomValue(marketPromotionSelectionState);
  const currentSelection = selection?.orgId === orgId ? selection : undefined;
  const { config } = usePromoteDays();
  const packageActivity = config?.activityType === 'PACKAGE_ACTIVITY';
  const shouldLoadDefaultPlan = validOrganization && packageActivity && !currentSelection;
  const { defaultZone } = usePriceZones({ enabled: shouldLoadDefaultPlan });
  const { catalog } = usePricingCatalog(defaultZone, 'ORDER', {
    enabled: shouldLoadDefaultPlan && defaultZone === 'CN',
  });
  const defaultPackage =
    catalog?.packages?.find(
      (item) => item.regionPackageId === catalog.recommendData?.recommendRegionPackageId,
    ) ?? catalog?.packages?.[0];
  const priceZone = currentSelection ? currentSelection.priceZone : defaultZone;
  const annualPackage = currentSelection ? currentSelection.annualPackage : defaultPackage;
  const active = useAnnualActivity(priceZone, annualPackage);
  const sameActivity = Number(config?.id) === annualPackage?.annualActivity?.id;
  const visible =
    validOrganization &&
    active &&
    packageActivity &&
    sameActivity &&
    i18nExist('promote.banner.text', PROMOTE_I18N_NS);
  const doubleEleven = /双(?:十一|11)|11\.11/.test(config?.activityName ?? '');
  return { visible, annualPackage, doubleEleven };
}
